import { track } from './amplitude';
import { POI_DETAIL_CLOSED } from './events';
import { currentSceneId } from './trackerContext';
import { $currentSight } from '../state/appState';
import type { SightId } from '../types';

/**
 * POI read-proxy. Дуэт с `POI Viewed` (открытие панели POI): здесь ловим
 * закрытие/переключение детальной панели и считаем dwell, нормированный на
 * длину контента, — прокси «реально прочитал vs скользнул по верхам».
 *
 * Панель = app-state `sight-details` / `$focusedSightId`. Открытие —
 * `userSelectedSight` (уже трекается как `POI Viewed`, отдельного open-события
 * не заводим). Закрытие — переход `$currentSight` в `null` ИЛИ на другой sight
 * (свитч A→B = закрытие A + открытие B). Поэтому слушаем единый источник
 * правды `$currentSight`, а не разрозненные open/close. Модель — `photoTracker`
 * (module-local сессия + `duration_ms` на закрытии).
 */

// Порог read_flag в ms на символ контента. Placeholder — hypothesis,
// recalibrate against the dwell/char distribution (gap between bounce and read).
export const READ_PROXY_MS_PER_CHAR = 30;

type OpenPanel = {
  openedAt: number;
  sightId: SightId;
  sceneId: string | null;
  contentLength: number;
};

let open: OpenPanel | null = null;

/**
 * Длина видимого текста POI. `description` уже локализован в стор под активную
 * локаль (`poiClient.mapSight`), так что меряем то, что реально видит юзер.
 * Снимаем лёгкую markdown/HTML-разметку, чтобы длина отражала прочитанное,
 * а не разметку (общего util'а в репо нет — мелкий локальный хелпер).
 */
export const visibleTextLength = (raw: unknown): number => {
  if (typeof raw !== 'string' || raw.length === 0) return 0;
  const text = raw
    .replace(/<[^>]+>/g, '') // HTML-теги
    .replace(/[*_`~#>[\]()!-]/g, '') // лёгкая markdown-разметка
    .replace(/\s+/g, ' ')
    .trim();
  return text.length;
};

/** read_flag: dwell, нормированный на длину контента, перешёл порог.
 *  content_length === 0 → читать нечего, всегда false. Вынесена для теста. */
export const computeReadFlag = (
  durationMs: number,
  contentLength: number,
): boolean =>
  contentLength > 0 && durationMs / contentLength >= READ_PROXY_MS_PER_CHAR;

const flushClose = (): void => {
  if (open === null) return;
  const durationMs = Math.round(performance.now() - open.openedAt);
  const readFlag = computeReadFlag(durationMs, open.contentLength);
  track(POI_DETAIL_CLOSED, {
    sight_id: open.sightId,
    scene_id: open.sceneId,
    duration_ms: durationMs,
    content_length: open.contentLength,
    read_flag: readFlag,
  });
  open = null;
};

export const startPoiDetailTracking = (): void => {
  $currentSight.watch((sight) => {
    // Та же панель, что уже открыта — изменение стора не про lifecycle панели.
    if (open !== null && sight !== null && sight.id === open.sightId) return;

    // Закрытие (sight === null) или свитч на другой sight — флашим открытую.
    if (open !== null) flushClose();

    // Открытие новой панели — content_length снимаем в момент открытия
    // (значение, которое юзер видит). read_flag сравним только ВНУТРИ локали:
    // ar/en длины одного POI различаются — сегментировать read-proxy по locale.
    if (sight !== null) {
      open = {
        openedAt: performance.now(),
        sightId: sight.id,
        sceneId: currentSceneId(),
        contentLength: visibleTextLength(sight.description),
      };
    }
  });

  // Чтение, оборвавшееся закрытием вкладки, — тоже реальное чтение.
  window.addEventListener('pagehide', () => {
    flushClose();
  });
};
